import { makeAutoObservable } from 'mobx';
import moment from 'moment';
import PricingSubscriptionPresenter from './PricingSubscriptionPresenter';
import {
	SUCCESS_700, PRIMARY_BASE, WARNING_700, ERROR,
	WARNING_200, ERROR_LIGHT, STRONG_ERROR
} from '../../styles/colors';
import { actionResultIsError } from '../../lib/reduxUtils';
import PopupFactory from '../../factories/PopupFactory';
import SnackbarFactory from '../../factories/SnackbarFactory';
import { PRICING_PLANS_AVAILABLE } from '../../config/global';

const COMMON_PLAN_BUTTON_DATA = { disabled: !PRICING_PLANS_AVAILABLE };
const PAID_PLAN_BUTTON_DATA = { ...COMMON_PLAN_BUTTON_DATA, text: 'Change Plan' };
const FREE_TRIAL_BUTTON_DATA = { ...COMMON_PLAN_BUTTON_DATA, type: 'filled', text: 'Buy Now' };
const SUSPENDED_WORKSPACE_BUTTON_DATA = { ...COMMON_PLAN_BUTTON_DATA, text: 'See Plans' };
const MARKED_FOR_CANCELLATION_WORKSPACE_BUTTON_DATA = { ...COMMON_PLAN_BUTTON_DATA, text: 'Reactivate Subscription' };

export default class PlanSettingsPresenter {
	constructor( {
		popupSystem,
		cancelSubscription,
		reactivateSubscription,
		createSubscription,
		workspace,
		fetchWorkspaces,
		snackbarSystem,
		downgradeSubscription,
		cancelDowngradeSubscription,
		history,
		memberAddOn,
		liveSeatsInUseCount
	} ) {
		this._popupSystem = popupSystem;
		this.cancelSubscription = cancelSubscription;
		this.reactivateSubscription = reactivateSubscription;
		this.createSubscription = createSubscription;
		this.workspace = workspace;
		this._fetchWorkspaces = fetchWorkspaces;
		this.snackbarSystem = snackbarSystem;
		this.downgradeSubscription = downgradeSubscription;
		this.cancelDowngradeSubscription = cancelDowngradeSubscription;
		this._history = history;
		this.memberAddOn = memberAddOn;
		this.liveSeatsInUseCount = liveSeatsInUseCount;

		this.subscriptionModalIsOpen = this.showSubscriptionModal;
		this.selectPlanMode = false;
		this.selectedPricingPlanPrice = null;
		this._checkoutPricingPlanPrice = null;
		this.isCheckoutOpen = false;
		makeAutoObservable( this, { _popupSystem: false } );
	}

	cancelSubscriptionAndShowConfirmation = () => {
		this.cancelSubscription( this.workspace?.id, this.workspace.subscription?.id )
			.then( () => {
				this._popupSystem.showDialog( {
					title: 'Subscription Cancelled',
					description: 'Your workspace will be suspended at end of the current billing period.'
				} );
			} );
	};

	onSubscriptionCancelClicked = () => {
		window.Intercom?.( 'trackEvent', 'cancelled subscription' );

		this._popupSystem.showDangerConfirmationDialog( {
			title: 'Cancel Subscription',
			description: 'Your workspace will be suspended and all collaborative features will be blocked at the end of the billing period (you will still be able to download your assets after that).',
			checkboxDescription: 'I understand what will happen and I still want to proceed',
			acceptText: 'Cancel Subscription',
			descriptionAlignment: 'center'
		} )
			.then( ( confirm ) => {
				if ( confirm ) {
					this.cancelSubscriptionAndShowConfirmation();
				}
				this._popupSystem.hideConfirmationDialog();
			} );
	};

	reactivateSubscriptionAndShowConfirmation = () => {
		this.reactivateSubscription( this.workspace?.id, this.workspace.subscription?.id )
			.then( () => {
				this._popupSystem.showDialog( {
					title: 'Subscription Reactivated',
					description: 'Your subscription will be renewed at the end\nof your current billing period.'
				} );
			} );
	};

	onReactivateSubscriptionClicked = () => {
		this._popupSystem.showDefaultConfirmationDialog( {
			title: 'Reactivate Subscription',
			description: "Your subscription will be automatically renewed at the end of the billing period and you will be able to continue using all VeryBusy's features inside your workspace.",
			acceptText: 'Reactivate Subscription',
			descriptionAlignment: 'center'
		} ).then( ( confirm ) => {
			if ( confirm ) {
				this.reactivateSubscriptionAndShowConfirmation();
			}
			this._popupSystem.hideConfirmationDialog();
		} );
	};

	downgradeSubscriptionAndShowSuccessMessage = () => {
		this.downgradeSubscription( this.workspace )
			.then( () => {
				SnackbarFactory.forDowngradeSubscriptionSuccess( this.snackbarSystem );
			} );
	};

	onDowngradeClicked = () => {
		PopupFactory.downgradeSubscriptionConfirmationPopup( this._popupSystem )
			.then( ( confirm ) => {
				if ( confirm ) {
					this.downgradeSubscriptionAndShowSuccessMessage();
				}
				this._popupSystem.hideConfirmationDialog();
				this.closeSubscriptionModal();
			} );
	};

	openSubscriptionModal = () => {
		this.selectPlanMode = false;
		this.subscriptionModalIsOpen = true;
	};

	closeSubscriptionModal = () => {
		this.subscriptionModalIsOpen = false;
		this.selectPlanMode = false;
	};

	onChangePlanButtonClicked = () => {
		this.selectPlanMode = true;
		this.subscriptionModalIsOpen = true;
	};

	openCheckout = ( planPrice = null ) => {
		this._checkoutPricingPlanPrice = planPrice;
		this.isCheckoutOpen = true;
	};

	closeCheckout = () => {
		this.isCheckoutOpen = false;
		this._checkoutPricingPlanPrice = null;
	};

	selectPlanInChangeMode = ( planPrice ) => {
		const planIncludedMembers = planPrice.includedMembers;
		const seatsInUse = this.liveSeatsInUseCount ?? this.workspace.seatsInUseCount;
		const hasAddOnSeats = ( this.membersAddOnQuantity || 0 ) > 0;
		const exceedsLimit = !planPrice.supportsAddOnMembers
			&& planIncludedMembers != null && ( seatsInUse > planIncludedMembers || hasAddOnSeats );
		if ( exceedsLimit ) {
			this._popupSystem.showDialog( {
				title: 'Remove Members First',
				description: `This plan only supports ${planIncludedMembers} ${planIncludedMembers === 1 ? 'member' : 'members'}. Please remove extra members and seats from your workspace before switching to this plan.`
			} );
			return;
		}
		this.selectedPricingPlanPrice = planPrice;
		this.closeSubscriptionModal();
	};

	onCancelDowngradeClicked = () => {
		this.cancelDowngradeSubscription( this.workspace.id )
			.then( () => {
				SnackbarFactory.forCancelDowngradeSubscriptionSuccess( this.snackbarSystem );
				this.closeSubscriptionModal();
			} );
	};

	onDowngradeWarningButtonClicked = () => (
		this.isSuspendedForDowngrade ? this.openSubscriptionModal() : this.onCancelDowngradeClicked()
	);

	createFreeSubscription( freePricingPlanPrice ) {
		if ( !freePricingPlanPrice?.isFree ) throw Error( 'A free pricing plan price must be provided.' );

		this.createSubscription( this.workspace.id, freePricingPlanPrice ).then( ( result ) => {
			if ( actionResultIsError( result ) ) {
				PopupFactory.defaultErrorDialog( this._popupSystem );
				return;
			}

			this._popupSystem.showDialog( {
				title: 'Workspace Reactivated',
				description: 'You can now use your workspace projects with collaboration features for free.'
			} );
			this._fetchWorkspaces();
		} );
	}

	onUpgradePlanButtonClicked = () => {
		if ( this.isSubscriptionMarkedForCancellation ) {
			return this.onReactivateSubscriptionClicked();
		}
		if ( this.workspace.subscription?.inStudioTrial ) {
			return this.openCheckout();
		}
		return this.openSubscriptionModal();
	}

	onGoToBillingClicked = () => {
		this._history.replace( `/user/workspaces/${this.workspace.id}/billing` );
	}

	openSubscriptionModalBasedOnQueryParams = () => {
		const queryParams = new URLSearchParams( this._history.location.search );
		if ( queryParams.get( 'modal' ) === 'subscription-selection' ) {
			this.openSubscriptionModal();
		}
	}

	get pricingSubscriptionPresenter() {
		return new PricingSubscriptionPresenter( { workspace: this.workspace } );
	}

	get projectsCount() {
		return this.workspace.projectsCount;
	}

	get isAPaidPlan() {
		return !!this.workspace?.hasAPaidPlan;
	}

	get isDowngradePending() {
		return this.workspace?.hasADowngradePending;
	}

	get isSuspendedForDowngrade() {
		return this.workspace.isSuspendedForDowngrade;
	}

	get shouldRenderDowngradeWarning() {
		return this.isSuspendedForDowngrade && this.isDowngradePending;
	}

	get shouldRenderCancelSubscriptionButton() {
		return this.workspace.isSubscriptionActive && !this.workspace.isOnFreeTrial;
	}

	get shouldRenderAddOnsDescription() {
		return this.workspace.hasMembersAddOns || this.workspace.hasStorageAddOns;
	}

	get downgradeWarningButtonText() {
		return this.isSuspendedForDowngrade ? 'See Plans' : 'Restore Studio';
	}

	get downgradeWarningText() {
		return this.isSuspendedForDowngrade ? 'To reactivate your workspace, please select a plan.'
			: `Reminder: You've elected to downgrade your plan. On ${this.formatedBillingPeriodEnd} your subscription will not be renewed, and your workspace will become suspended. On this day, sign up for any plan to reactivate it.`;
	}

	get downgradeWarningIconColor() {
		return this.isSuspendedForDowngrade ? ERROR : WARNING_700;
	}

	get downgradeWarningColor() {
		return this.isSuspendedForDowngrade ? ERROR_LIGHT : WARNING_200;
	}

	get downgradeWarningTooltipText() {
		return this.isSuspendedForDowngrade ? 'You must be within the limits of the plan to which you want to subscribe.'
			: 'You will need to be within the storage and member limits of the new plan to be able to downgrade.';
	}

	get effectivePricingPlanPrice() {
		return this.selectedPricingPlanPrice || this.workspace.pricingPlanPrice;
	}

	get canAddMembersAddOn() {
		return this.effectivePricingPlanPrice?.supportsAddOnMembers ?? true;
	}

	get checkoutPricingPlanPrice() {
		return this._checkoutPricingPlanPrice || this.effectivePricingPlanPrice;
	}

	get checkoutTotalCost() {
		const planCost = this.checkoutPricingPlanPrice.cost;
		if ( !this.workspace.subscription?.inStudioTrial || !this.memberAddOn ) return planCost;
		const quantity = this.membersAddOnQuantity || 0;
		if ( !quantity ) return planCost;
		const seatsCost = this._isEffectivelyAnnual
			? this.memberAddOn.annualPriceCost * quantity
			: this.memberAddOn.monthlyPriceCost * quantity;
		return planCost + seatsCost;
	}

	get planName() {
		if ( this.workspace.subscription?.inStudioTrial ) {
			return this.effectivePricingPlanPrice?.planName?.capitalize() || '';
		}
		return this.workspace.subscriptionPlanName?.capitalize() || '';
	}

	get freeTrialPlanName() {
		return `${this.planName.replace( ' Plan', '' )} Trial`;
	}

	get isSubscriptionActive() {
		return this.workspace.isSubscriptionActive;
	}

	get isOnActiveFreeTrial() {
		return this.workspace.isOnActiveFreeTrial;
	}

	get upgradeButtonData() {
		if ( this.workspace.isOnActiveFreeTrial ) return FREE_TRIAL_BUTTON_DATA;
		if ( !this.workspace.isSubscriptionActive ) return SUSPENDED_WORKSPACE_BUTTON_DATA;
		if ( this.isSubscriptionMarkedForCancellation ) return MARKED_FOR_CANCELLATION_WORKSPACE_BUTTON_DATA;
		return PAID_PLAN_BUTTON_DATA;
	}

	get isSubscriptionMarkedForCancellation() {
		return this.workspace.isSubscriptionMarkedForCancellation;
	}

	get titleColor() {
		return PRIMARY_BASE;
	}

	get formatedBillingPeriodEnd() {
		return moment.utc( this.workspace.billingPeriodEndsAt ).format( 'MMM DD, YYYY' );
	}

	get showSubscriptionModal() {
		return this._history.location?.state?.showSubscriptionModal;
	}

	get expiredColor() {
		return STRONG_ERROR;
	}

	get freeTrialColor() {
		return SUCCESS_700;
	}

	get daysLeftOnFreeTrial() {
		return this.workspace.daysLeftOnFreeTrial;
	}

	get _effectivePlanIncludedMembers() {
		return this.effectivePricingPlanPrice?.includedMembers;
	}

	get trialSeatsAdded() {
		return this.memberAddOnQuantity || 0;
	}

	get memberAddOnQuantity() {
		if ( !this.workspace.subscription?.inStudioTrial ) return null;
		return this.workspace.subscription.membersAddOnQuantity ?? 0;
	}

	get subscriptionPriceWithDiscount() {
		return `$${this.workspace.subscription.priceWithDiscountForDisplay}`;
	}

	get planCostForDisplay() {
		const cost = this.workspace.subscription?.inStudioTrial
			? this.effectivePricingPlanPrice?.cost
			: this.workspace.subscriptionPlanCost;
		return `$${this._formatAmount( this._toMonthly( cost ) )}`;
	}

	get planRowLabel() {
		const seats = this.planSeatsIncluded;
		if ( seats == null ) return this.planName;
		return `${this.planName} - ${seats} ${seats === 1 ? 'seat' : 'seats'} included`;
	}

	get planSeatsIncluded() {
		return this._effectivePlanIncludedMembers;
	}

	get hasMembersAddOn() {
		return this.workspace.hasMembersAddOns;
	}

	get membersAddOnQuantity() {
		return this.workspace.subscription?.membersAddOnQuantity;
	}

	get membersAddOnRowLabel() {
		return this.membersAddOnQuantity
			? `Extra Seat (${this.membersAddOnQuantity} x ${this.membersAddOnUnitCostForDisplay})`
			: 'Extra Seat';
	}

	get membersAddOnCostForDisplay() {
		if ( this.workspace.subscription?.inStudioTrial && this.memberAddOn ) {
			return `$${this._formatAmount( this._effectiveMembersAddOnUnitCost * ( this.membersAddOnQuantity || 0 ) )}`;
		}
		return `$${this._formatAmount( this._toMonthly( this.workspace.membersAddOnsCost ) )}`;
	}

	get membersAddOnUnitCostForDisplay() {
		if ( this.workspace.subscription?.inStudioTrial && this.memberAddOn ) {
			return `$${this._formatAmount( this._effectiveMembersAddOnUnitCost )}`;
		}
		const quantity = this.workspace.membersAddOnQuantity;
		const totalCost = this.workspace.membersAddOnsCost;
		if ( !quantity || !totalCost ) return '$0';
		return `$${this._formatAmount( this._toMonthly( totalCost / quantity ) )}`;
	}

	get _effectiveMembersAddOnUnitCost() {
		if ( this.effectivePricingPlanPrice?.isAnnualPrice ) {
			return ( this.memberAddOn?.annualPriceCost || 0 ) / 12;
		}
		return this.memberAddOn?.monthlyPriceCost || 0;
	}

	get hasStorageAddOn() {
		return this.workspace.hasStorageAddOns;
	}

	get storageAddOnQuantity() {
		return this.workspace.subscription?.storageAddOnQuantity;
	}

	get storageAddOnRowLabel() {
		return this.storageAddOnQuantity
			? `Extra Storage - 250 GB (${this.storageAddOnQuantity} x ${this.storageAddOnUnitCostForDisplay})`
			: 'Extra Storage - 250 GB';
	}

	get storageAddOnCostForDisplay() {
		return `$${this._formatAmount( this._toMonthly( this.workspace.storageAddOnsCost ) )}`;
	}

	get storageAddOnIncludedAmountForDisplay() {
		return this.workspace.storageAddOnIncludedAmount?.formatBytes?.();
	}

	get storageAddOnUnitCostForDisplay() {
		const quantity = this.workspace.storageAddOnQuantity;
		const totalCost = this.workspace.storageAddOnsCost;
		if ( !quantity || !totalCost ) return '$0';
		return `$${this._formatAmount( this._toMonthly( totalCost / quantity ) )}`;
	}

	get totalCostForDisplay() {
		const membersAddOnsCost = this.workspace.subscription?.inStudioTrial && this.memberAddOn
			? this._effectiveMembersAddOnUnitCost * this.membersAddOnQuantity
			: this._toMonthly( this.workspace.membersAddOnsCost );
		const storageAddOnsCost = this._toMonthly( this.workspace.storageAddOnsCost );
		const planCost = this._toMonthly( this.workspace.subscription?.inStudioTrial
			? this.effectivePricingPlanPrice?.cost
			: this.workspace.subscriptionPlanCost
		);
		return `$${this._formatAmount( planCost + membersAddOnsCost + storageAddOnsCost )}`;
	}

	get billingNote() {
		return this._isEffectivelyAnnual
			? 'Billed annually. Includes 15% discount.'
			: 'Billed monthly. Get 15% off annually.';
	}

	get _isEffectivelyAnnual() {
		if ( this.workspace.subscription?.inStudioTrial ) {
			return this.effectivePricingPlanPrice?.isAnnualPrice || false;
		}
		return this.workspace.hasYearlySubscriptionInterval;
	}

	_toMonthly( cost ) {
		return this._isEffectivelyAnnual ? ( cost || 0 ) / 12 : ( cost || 0 );
	}

	_formatAmount( amount ) {
		return parseFloat( amount.toFixed( 2 ) );
	}
}
